import { lazy, Suspense, useRef, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { Button, Spinner } from '@leanin/ui';
import { Popover, Tooltip } from '@leanin/ui/client';
import { useTranslations } from '@/i18n/translations';
import styles from './EmojiPicker.module.css';

// Emoji button + searchable picker popover for text composers. The picker
// module (and emoji-picker-react with it) is fetched on first open. The
// glyph is imported straight from lucide-react — a feature ornament, not a
// curated `I` design-system icon.

const EmojiPickerPanel = lazy(() => import('./EmojiPickerPanel'));

type Props = {
  /** Called with the picked emoji; the popover closes after it returns. The
   *  caller inserts it (and moves focus back into its editor). */
  readonly onPick: (emoji: string) => void;
  readonly disabled?: boolean;
};

export function EmojiPickerButton({ onPick, disabled = false }: Props) {
  const t = useTranslations('Editor');
  const [open, setOpen] = useState(false);
  // A pick hands focus to the caller's editor; skip Radix's restore-to-
  // trigger so it doesn't steal focus back on close. Escape / outside-click
  // closes still return focus to the button.
  const pickedRef = useRef(false);
  const label = t('emojiAria');

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="top"
      align="end"
      onCloseAutoFocus={(event) => {
        if (!pickedRef.current) return;
        pickedRef.current = false;
        event.preventDefault();
      }}
      trigger={
        <Tooltip content={label}>
          <Button
            variant="ghost"
            size="sm"
            icon={<SmilePlus size="100%" strokeWidth={2} />}
            aria-label={label}
            disabled={disabled}
          />
        </Tooltip>
      }
    >
      {open ? (
        <Suspense
          fallback={
            <div className={styles['loading']}>
              <Spinner label={t('emojiPicker.loading')} />
            </div>
          }
        >
          <EmojiPickerPanel
            onPick={(emoji) => {
              pickedRef.current = true;
              onPick(emoji);
              setOpen(false);
            }}
          />
        </Suspense>
      ) : null}
    </Popover>
  );
}
